import {
  formatDateTimeRangeLabel,
  type PickupOptionItem,
} from './preorderUtils';

export interface PreorderHeaderCampaign {
  id: string | number;
  campaignName: string;
  startsAtTaipei: string;
  endsAtTaipei: string;
  campaignCopy?: string | null;
  pickupDates?: string[];
  bannerImageUrl?: string | null;
}

export interface PreorderHeaderProps {
  campaign: PreorderHeaderCampaign;
  showBackButton: boolean;
  onBackToCampaignList: () => void;
  pickupDateOptions: PickupOptionItem[];
  selectedPickupDate: string;
  onPickupDateChange: (value: string) => void;
  normalizeImageUrl: (url: string | null | undefined) => string | null;
}

/**
 * 單一檔期時的頂部（banner + 標題 + 返回活動列表 + 面交日 tab）
 */
export function PreorderHeader({
  campaign,
  showBackButton,
  onBackToCampaignList,
  pickupDateOptions,
  selectedPickupDate,
  onPickupDateChange,
  normalizeImageUrl,
}: PreorderHeaderProps) {
  const campaignBannerUrl = (campaign as { bannerImageUrl?: string | null })
    .bannerImageUrl;

  return (
    <div
      className="shadow-sm sticky top-0 z-10 border-b border-gray-200 relative"
      style={
        campaignBannerUrl
          ? {
              backgroundImage: `url(${normalizeImageUrl(campaignBannerUrl)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : { backgroundColor: 'white' }
      }
    >
      {campaignBannerUrl ? (
        <span
          className="absolute inset-0 bg-black/35 pointer-events-none"
          aria-hidden
        />
      ) : null}
      <div className="relative z-10 px-4 py-3">
        {showBackButton ? (
          <button
            type="button"
            onClick={onBackToCampaignList}
            className="text-sm text-blue-200 hover:text-white mb-1 flex items-center gap-1 drop-shadow"
          >
            ← 返回活動列表
          </button>
        ) : null}
        <h1
          className={
            campaignBannerUrl
              ? 'text-[1.25rem] font-bold text-white drop-shadow-md'
              : 'text-[1.25rem] font-bold text-gray-900'
          }
        >
          {campaign.campaignName}
        </h1>
        <p
          className={
            campaignBannerUrl
              ? 'text-[0.75rem] text-white/90 font-normal mt-1 drop-shadow'
              : 'text-[0.75rem] text-gray-500 font-normal mt-1'
          }
        >
          {formatDateTimeRangeLabel(campaign.startsAtTaipei)} 至{' '}
          {formatDateTimeRangeLabel(campaign.endsAtTaipei)}
        </p>
        <p
          className={
            campaignBannerUrl
              ? 'text-[0.875rem] text-white/90 font-medium mt-1 line-clamp-2 drop-shadow'
              : 'text-[0.875rem] text-gray-500 font-medium mt-1 line-clamp-2'
          }
        >
          {campaign.campaignCopy}
        </p>
      </div>
      {campaign.pickupDates?.length ? (
        <div className="relative z-10 px-2 pb-2 overflow-x-auto overflow-y-visible">
          <div className="flex gap-2 min-w-0">
            {pickupDateOptions.map(option => {
              const isSelected = selectedPickupDate === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    if (option.value === selectedPickupDate) return;
                    onPickupDateChange(option.value);
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer touch-manipulation ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
